"use client";

import type { ContentDocument, ContentResponse } from "@/app/lib/types";
import { fetchContent } from "@/app/lib/api";

export const DEFAULT_CONTENT: Record<string, ContentDocument> = {
  "usage.judging": {
    blocks: [
      { id: "win-explainer", text: "**% Win** — overall fraction of judging runs this provider was named the winner.", placeholders: [] },
      { id: "bias-check-explainer", text: "**% Bias Check** — when this provider scored a panel, how often did it give its own family the (unique) top score?", placeholders: [] },
      { id: "oai-premium-on-oai-explainer", text: "**OAI premium on OAI** — OAI's mean of OAI scores minus Gemini and Anthropic's mean of OAI scores.", placeholders: [] },
      { id: "openai-premium-vs-others-explainer", text: "**OpenAI premium vs others** — OAI's mean of OAI scores minus OAI's mean of scores for Gemini and Anthropic.", placeholders: [] },
      { id: "trio-oai-self-penalty-explainer", text: "**Trio OAI self-penalty** — Trio reduction of OAI's score it gives itself when judging, to account for bias. This adjustment is only reflected in the % Win column above.", placeholders: [] },
      { id: "window-caveat", text: "Based on {panel_runs} panel-mode {runs_word} in this window. Bias metrics only reflect panel-mode entries (post 2026-05-05); windows extending back further may show identical bias numbers because the panel-era subset is the same.", placeholders: ["panel_runs", "runs_word"] },
    ],
    columns: [
      { id: "provider", label: "Provider", align: "leading", width_hint: 0 },
      { id: "win_pct", label: "% Win", align: "trailing", width_hint: 50 },
      { id: "avg_score", label: "Avg", align: "trailing", width_hint: 42 },
      { id: "bias_pct", label: "% Bias", align: "trailing", width_hint: 52 },
      { id: "avg_self", label: "Avg Self", align: "trailing", width_hint: 56 },
    ],
  },
};

const CACHE_KEY = "trio_content";
let _memo: Record<string, ContentDocument> | null = null;
const listeners = new Set<() => void>();

function isUsableDocument(raw: unknown): raw is ContentDocument {
  if (!raw || typeof raw !== "object") return false;
  const document = raw as Partial<ContentDocument>;
  return Array.isArray(document.blocks) && document.blocks.length > 0 &&
    Array.isArray(document.columns) && document.columns.length > 0 &&
    document.blocks.every((block) => typeof block?.id === "string" && block.id.trim() && typeof block.text === "string" && block.text.trim()) &&
    document.columns.every((column) => typeof column?.id === "string" && column.id.trim() && typeof column.label === "string" && column.label.trim());
}

function isUsableHelpDocument(raw: unknown): raw is ContentDocument {
  if (!raw || typeof raw !== "object") return false;
  const document = raw as { blocks?: unknown; columns?: unknown };
  if (!Array.isArray(document.blocks) || document.blocks.length === 0 || !Array.isArray(document.columns)) return false;
  return document.blocks.every((block) => {
    if (!block || typeof block !== "object") return false;
    const candidate = block as { id?: unknown; type?: unknown; text?: unknown; style?: unknown; columns?: unknown; rows?: unknown; ordered?: unknown; items?: unknown };
    if (typeof candidate.id !== "string" || !candidate.id.trim()) return false;
    if (candidate.type === "table") {
      const columns = candidate.columns;
      if (!Array.isArray(columns) || columns.length === 0 || !columns.every((column) => typeof column === "string" && column.trim())) return false;
      return Array.isArray(candidate.rows) && candidate.rows.length > 0 &&
        candidate.rows.every((row) => {
          if (!row || typeof row !== "object") return false;
          const tableRow = row as { id?: unknown; cells?: unknown };
          return typeof tableRow.id === "string" && tableRow.id.trim() &&
            Array.isArray(tableRow.cells) && tableRow.cells.length === columns.length &&
            tableRow.cells.every((cell) => typeof cell === "string");
        });
    }
    if (candidate.type === "list") {
      return typeof candidate.ordered === "boolean" &&
        Array.isArray(candidate.items) && candidate.items.length > 0 &&
        candidate.items.every((item) => typeof item === "string" && item.trim());
    }
    return (candidate.type === "paragraph" || candidate.type === "heading" || candidate.type === "callout") &&
      typeof candidate.text === "string" && candidate.text.trim() &&
      (candidate.type !== "callout" || candidate.style === "info" || candidate.style === "warning");
  });
}

function usableDocuments(raw: unknown): Record<string, ContentDocument> | null {
  if (!raw || typeof raw !== "object") return null;
  const documents = (raw as Partial<ContentResponse>).documents;
  const judging = documents?.["usage.judging"];
  const usable: Record<string, ContentDocument> = {};
  if (isUsableDocument(judging)) usable["usage.judging"] = judging;
  for (const id of ["help.quick", "help.details", "help.complete"]) {
    const document = documents?.[id];
    if (isUsableHelpDocument(document)) usable[id] = document;
  }
  return usable;
}

/** Synchronous cache-or-default read. Safe during render and never throws. */
export function getContent(): Record<string, ContentDocument> {
  if (_memo) return _memo;
  if (typeof window === "undefined") return DEFAULT_CONTENT;
  try {
    const cached = window.localStorage.getItem(CACHE_KEY);
    const documents = cached ? usableDocuments(JSON.parse(cached)) : null;
    if (documents) {
      _memo = { ...DEFAULT_CONTENT, ...documents };
      return _memo;
    }
  } catch {
    /* private mode or corrupt cache — use shipped words */
  }
  return DEFAULT_CONTENT;
}

/** Subscribe to successful content replacements so mounted views can update. */
export function subscribeToContent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Fetch and cache content best-effort; failures leave cache/defaults intact. */
export async function refreshContent(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const documents = usableDocuments(await fetchContent());
    if (!documents) return;
    const mergedDocuments = { ...getContent(), ...documents };
    _memo = mergedDocuments;
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify({ documents: mergedDocuments }));
    } catch {
      /* quota / private mode — memo still applies for this session */
    }
    listeners.forEach((listener) => listener());
  } catch (err) {
    console.warn("[content] refresh failed; using cache/defaults:", err);
  }
}
